import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import PatientFormsViewTopBar from '@components/pages/FormBuilder/PatientFormsView/PatientFormsViewTopBar.component'
import PatientFormsViewSidebar from '@components/pages/FormBuilder/PatientFormsView/PatientFormsViewSidebar.component'
import PatientFormsViewMainContent from '@components/pages/FormBuilder/PatientFormsView/PatientFormsViewMainContent.component'
import type {
  PatientFormDocumentView,
  PatientProcedureEntryWithId,
} from '@components/pages/FormBuilder/PatientFormsView/mockData'
import {
  getFormTemplateById,
  getPendingFormEntries,
} from '@services/api/forms.api'
import {
  formDataToSchemaEntry,
  schemaJsonToGroups,
} from '@components/pages/FormBuilder/SendingFormsTable/formTemplateToFormData'
import { EmptyState, type FilterSelection } from '@enamelai/design-system'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import './PatientFormsView.css'

const PATIENT_FORMS_VIEW_LOADING_CONTAINER = 'body'

interface LocationState {
  patientName?: string
}

interface PatientFormsViewProps {
  patientId?: string
  patientName?: string
  onBackToDashboard?: (options?: { tab?: 'pending' | 'completed' }) => void
}

const PatientFormsView: React.FC<PatientFormsViewProps> = ({
  patientId: propPatientId,
  patientName: propPatientName,
  onBackToDashboard,
}) => {
  const { patientId: routePatientId } = useParams<{ patientId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state || {}) as LocationState

  const [pendingEntry, setPendingEntry] = useState<{
    patientName: string
    procedures: PatientProcedureEntryWithId[]
    documents: PatientFormDocumentView[]
  } | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [selectedProcedureId, setSelectedProcedureId] = useState<string | null>(
    null,
  )
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [filters, setFilters] = useState<FilterSelection>({
    date: [],
    tag: [],
  })
  const [viewingDocument, setViewingDocument] =
    useState<PatientFormDocumentView | null>(null)
  const [pdfStatusRefresh, setPdfStatusRefresh] = useState(0)
  const loadedPatientIdRef = useRef<string | undefined>(undefined)

  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const effectivePatientId = propPatientId ?? routePatientId

  const patientName =
    pendingEntry?.patientName ??
    propPatientName ??
    state.patientName ??
    'Patient'

  useEffect(() => {
    if (!effectivePatientId) {
      setPendingEntry(null)
      setLoading(false)
      loadedPatientIdRef.current = undefined
      return
    }
    const isInitialLoad = loadedPatientIdRef.current !== effectivePatientId
    if (isInitialLoad) {
      setLoading(true)
      showLoading({ container: PATIENT_FORMS_VIEW_LOADING_CONTAINER })
    }
    getPendingFormEntries({ patient_id: effectivePatientId })
      .then(async (entries) => {
        const entry =
          entries.find((e) => e.patientId === effectivePatientId) ?? null
        if (!entry) {
          setPendingEntry(null)
          return
        }
        const procedures: PatientProcedureEntryWithId[] = entry.appointments
          .filter(
            (apt) =>
              (apt.completedForms?.length ?? 0) > 0 ||
              (apt.completedFormsCount ?? 0) > 0,
          )
          .map((apt) => ({
            id: apt.appointmentId || 'direct',
            date: apt.appointmentDate,
            procedureName: apt.appointmentType,
          }))
          .sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          )
        const documents: PatientFormDocumentView[] = []
        for (const apt of entry.appointments) {
          const completed = apt.completedForms ?? []
          const incomplete = apt.incompleteForms ?? []
          const appointmentDate = apt.appointmentDate
          for (const form of completed) {
            const { groups, fieldValues } = formDataToSchemaEntry(form)
            documents.push({
              id: form.assignmentId ?? form.id,
              title: form.name,
              dateSent: form.dateSent ?? appointmentDate,
              dateCompleted: form.dateCompleted ?? '',
              procedureEntryId: apt.appointmentId || 'direct',
              completed: true,
              groups,
              fieldValues,
              assignmentId: form.assignmentId,
              signedPdfBlobName: form.signedPdfBlobName,
              signedPdfStatus: form.signedPdfStatus,
              signedPdfLastError: form.signedPdfLastError,
            })
          }
          const incompleteDocuments = await Promise.all(
            incomplete.map(async (form) => {
              const formTemplate = await getFormTemplateById(form.id).catch(
                () => null,
              )
              const groups = schemaJsonToGroups(formTemplate?.schema_json)
              return {
                id: form.assignmentId ?? form.id,
                title: form.name,
                dateSent: form.dateSent ?? appointmentDate,
                dateCompleted: '',
                procedureEntryId: apt.appointmentId || 'direct',
                completed: false,
                groups,
                fieldValues: {},
              }
            }),
          )
          for (const incompleteDocument of incompleteDocuments) {
            documents.push(incompleteDocument)
          }
        }
        setPendingEntry({
          patientName: entry.patientName,
          procedures,
          documents,
        })
      })
      .catch(() => {
        if (isInitialLoad) setPendingEntry(null)
      })
      .finally(() => {
        loadedPatientIdRef.current = effectivePatientId
        if (isInitialLoad) {
          hideLoading(PATIENT_FORMS_VIEW_LOADING_CONTAINER)
          setLoading(false)
        }
      })
  }, [effectivePatientId, showLoading, hideLoading, pdfStatusRefresh])

  const hasProcessingPdf =
    pendingEntry?.documents.some(
      (document) => document.signedPdfStatus === 'pending',
    ) ?? false

  useEffect(() => {
    if (!hasProcessingPdf) return
    const interval = window.setInterval(() => {
      setPdfStatusRefresh((value) => value + 1)
    }, 3000)
    return () => window.clearInterval(interval)
  }, [hasProcessingPdf])

  const handlePdfRegenerationStarted = useCallback((assignmentId: string) => {
    setPendingEntry((previous) => {
      if (!previous) return previous
      return {
        ...previous,
        documents: previous.documents.map((document) =>
          document.assignmentId === assignmentId
            ? { ...document, signedPdfStatus: 'pending' }
            : document,
        ),
      }
    })
  }, [])

  const handleSelectProcedure = useCallback((id: string) => {
    setSelectedProcedureId(id)
  }, [])

  const documentsForMainTable = useMemo(() => {
    if (!pendingEntry) return []
    if (!selectedProcedureId) return []
    return pendingEntry.documents.filter(
      (doc) => doc.procedureEntryId === selectedProcedureId,
    )
  }, [pendingEntry, selectedProcedureId])

  useEffect(() => {
    if (!pendingEntry?.procedures.length) {
      setSelectedProcedureId(null)
      return
    }
    setSelectedProcedureId((prev) => {
      if (
        prev &&
        pendingEntry.procedures.some((procedure) => procedure.id === prev)
      ) {
        return prev
      }
      return pendingEntry.procedures[0].id
    })
  }, [pendingEntry])

  const selectedProcedureName = useMemo(
    () =>
      pendingEntry?.procedures.find(
        (procedure) => procedure.id === selectedProcedureId,
      )?.procedureName,
    [pendingEntry, selectedProcedureId],
  )

  const filterSelectionList = useMemo(() => {
    const dateOptions = new Set<string>()
    documentsForMainTable.forEach((doc) => {
      const d = new Date(doc.dateSent)
      const monthYear = d.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      })
      dateOptions.add(monthYear)
    })
    return {
      date: Array.from(dateOptions).sort(),
      tag: [] as string[],
    }
  }, [documentsForMainTable])

  const filteredDocuments = useMemo(() => {
    return documentsForMainTable.filter((doc) => {
      const matchesSearch =
        !searchQuery ||
        doc.title.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesDate =
        !filters.date?.length ||
        filters.date.some((selectedDate: string) => {
          const docDate = new Date(doc.dateSent)
          const docMonthYear = docDate.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })
          return docMonthYear === selectedDate
        })
      return matchesSearch && matchesDate
    })
  }, [documentsForMainTable, searchQuery, filters])

  const handleBack = useCallback(() => {
    if (onBackToDashboard) {
      onBackToDashboard({ tab: 'completed' })
      return
    }
    navigate('/form-builder', { state: { tab: 'completed' } })
  }, [navigate, onBackToDashboard])

  const handleCreateForm = useCallback(() => {
    navigate('/form-builder/create')
  }, [navigate])

  const handleFilterChange = useCallback((selectedFilters: FilterSelection) => {
    setFilters((prev) => ({
      date: [],
      tag: [],
      ...prev,
      ...selectedFilters,
    }))
  }, [])

  const handleResetFilters = useCallback(() => {
    setSearchQuery('')
    setFilters({ date: [], tag: [] })
  }, [])

  if (loading) return null

  if (!effectivePatientId || !pendingEntry) {
    return (
      <div className="pfv-container-fluid">
        <div className="pfv-layout">
          <div className="pfv-container">
            <div className="pfv-outer">
              <EmptyState
                className="pfv-empty-state"
                title="No patient selected"
                description="Select an appointment from the Pending Forms tab to view completed forms."
                outlined
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pfv-container-fluid">
      <div className="pfv-layout">
        <PatientFormsViewSidebar
          procedures={pendingEntry.procedures}
          selectedProcedureId={selectedProcedureId}
          onSelectProcedure={handleSelectProcedure}
          sidebarSearchQuery={sidebarSearchQuery}
          onSidebarSearch={setSidebarSearchQuery}
        />
        <div className="pfv-container">
          <div className="pfv-outer">
            <PatientFormsViewTopBar
              patientName={patientName}
              appointmentName={selectedProcedureName}
              onBack={handleBack}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              filterSelectionList={filterSelectionList}
              onCreateForm={handleCreateForm}
            />
            <div className="pfv-inner">
              <PatientFormsViewMainContent
                documents={filteredDocuments}
                viewingDocument={viewingDocument}
                patientName={patientName}
                onViewDocument={setViewingDocument}
                onClosePreview={() => setViewingDocument(null)}
                onPdfRegenerationStarted={handlePdfRegenerationStarted}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatientFormsView
